import { DEFAULT_LOCALE, FALLBACK_LOCALE } from "../constants/locales.js";
import { POINTS_PER_USD } from "../constants/points.js";

/**
 * Форматирование чисел, денег и Баллов. Правила проекта:
 * `1 234 567,89` (ru) / `1,234,567.89` (en); валюта — только USD (`$12.50`).
 */

/**
 * Набор полей даты по правилу проекта: `23.02.2026` (ru) / `Feb 23, 2026` (en).
 *
 * Живёт здесь, а не в `lib/format.ts` каждого приложения, потому что это
 * ПРАВИЛО, а не форматтер: копий было три, правило знала одна
 * (`sso-web/lib/format-date.ts`), а витрина и админка форматировали `month:
 * "short"` на всех локалях — и русская дата выходила «25 апр. 2026 г.» вопреки
 * собственному доккомментарию. Сам `Intl.DateTimeFormat`/`DateFormatter`
 * остаётся в приложениях: `@internationalized/date` не должен приезжать в
 * пакет, который читает и сервер.
 *
 * Почему у английского месяц словом, а у остальных цифрами: `02.03` читается
 * как 2 марта у одних и как 3 февраля у других, и в англоязычном тексте эта
 * двусмысленность стоит дороже компактности.
 */
export function numericDateOptions(locale: string): Intl.DateTimeFormatOptions {
	return locale.startsWith("en")
		? { year: "numeric", month: "short", day: "numeric" }
		: { year: "numeric", month: "2-digit", day: "2-digit" };
}

const numberFormatters = new Map<string, Intl.NumberFormat>();

function formatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
	const key = `${locale}:${JSON.stringify(options)}`;
	let cached = numberFormatters.get(key);
	if (!cached) {
		cached = new Intl.NumberFormat(locale, options);
		numberFormatters.set(key, cached);
	}
	return cached;
}

/** Баллы: целое с неразрывными разделителями групп — «12 480». */
export function formatPoints(points: number, locale: string = DEFAULT_LOCALE): string {
	return formatter(locale, { maximumFractionDigits: 0 }).format(points);
}

/** Со знаком: «+14 000» / «−8 950» (минус — типографский U+2212). */
export function formatPointsSigned(points: number, locale: string = DEFAULT_LOCALE): string {
	const formatted = formatPoints(Math.abs(points), locale);
	if (points > 0) return `+${formatted}`;
	if (points < 0) return `−${formatted}`;
	return formatted;
}

/** Деньги из minor units: 12480 → «$124.80». */
export function formatMoney(
	minor: number,
	currency = "USD",
	locale: string = DEFAULT_LOCALE,
): string {
	// Цены экосистемы published в USD и выглядят одинаково на всех локалях:
	// `$179.00`, а не `179,00 $` (CLAUDE.md §Форматирование). Русская запись
	// доллара читается как локальная валюта и сбивает восприятие цены.
	if (currency === "USD") {
		return formatter("en-US", { style: "currency", currency: "USD" }).format(minor / 100);
	}
	return formatter(locale, { style: "currency", currency }).format(minor / 100);
}

/** Стоимость Баллов деньгами по фиксированному курсу 100 = $1. */
export function pointsWorth(points: number, locale: string = DEFAULT_LOCALE): string {
	return formatMoney(Math.round((points / POINTS_PER_USD) * 100), "USD", locale);
}

/**
 * Перечисление через запятую и «и» на языке пользователя: «StudyHub, Hero и Портал».
 *
 * Обёртка, а не прямой `Intl.ListFormat`: конструктор бросает `RangeError` на
 * локали, которой нет в ICU конкретной сборки движка, и падает не подпись, а
 * весь экран (аудит фронта В11 — `continue-step.tsx` звал его напрямую; с двумя
 * локалями это не всплывало, с двадцатью одной всплывёт). Фолбэк — английские
 * правила, а не склейка запятыми: «A, B, C» без союза читается как оборванный
 * список.
 */
export function formatList(
	items: readonly string[],
	locale: string = DEFAULT_LOCALE,
	type: "conjunction" | "disjunction" = "conjunction",
): string {
	const values = items.filter((item) => item.length > 0);
	if (values.length === 0) return "";
	if (values.length === 1) return values[0] ?? "";
	try {
		return new Intl.ListFormat(locale, { style: "long", type }).format(values);
	} catch {
		try {
			return new Intl.ListFormat(FALLBACK_LOCALE, { style: "long", type }).format(values);
		} catch {
			// Движок без ListFormat вообще (старые сборки Node без full-icu).
			return values.join(", ");
		}
	}
}

/** Маскирование почты для экранов до входа: `maya@iec-mail.com` → `m•••a@iec-mail.com`. */
export function maskEmail(email: string): string {
	const [local = "", domain = ""] = email.split("@");
	if (local.length <= 2) return `${local.slice(0, 1)}•••@${domain}`;
	return `${local.slice(0, 1)}•••${local.slice(-1)}@${domain}`;
}
